import { Fragment, useMemo, useState } from 'react';
import Select, { SelectProps } from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import ListSubheader from '@material-ui/core/ListSubheader';
import InputLabel from '@material-ui/core/InputLabel';
import makeStyles from '@material-ui/core/styles/makeStyles';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import clsx from 'clsx';

import { useForm, useFormRefs, useFormRules, useGetRuleName, useTextFieldError, useGDirection, useGi18n } from '../hooks';
import { Group, GroupsRule, Option, OptionsRule } from '../types';
import { dirStyleGenerator } from '../functions';

import { useFormInputStyles } from './FormInput';
import FormHelperText from './FormHelperText';
import { get as _get } from 'lodash';

export interface FormSelectProps extends SelectProps {
	id: string;
	description: string;
	options?: Option[];
	groups?: Group[];
}

const FormSelect: React.FC<FormSelectProps> = ({ id, dir, description, options: o, groups: g, ...rest }) => {
	const [containerTarget, setContainerTarget] = useState<HTMLDivElement | null>(null);

	const formInputClasses = useFormInputStyles();

	const fieldError = useTextFieldError(id);
	const getRuleName = useGetRuleName();
	const direction = useGDirection();
	const formRules = useFormRules();
	const innerRef = useFormRefs();
	const form = useForm();

	const c = useGi18n()(i18n => i18n.error?.form?.choose);

	const ruleName = useMemo(() => {
		return getRuleName(formRules[id] as OptionsRule);
	}, [formRules, getRuleName, id]);

	const options = useMemo(() => {
		return o ?? (formRules[id] as OptionsRule)?.options;
	}, [formRules, o, id]);

	const groups = useMemo(() => {
		return g ?? (formRules[id] as GroupsRule)?.groups;
	}, [formRules, g, id]);

	const groupOptions = useMemo(() => {
		if (!groups) return;

		const groupOptions = [];

		for (const group of groups) {
			groupOptions.push({ type: 'header', value: group.header });
			groupOptions.push(...group.options);
		}

		return groupOptions;
	}, [groups]);

	const classes = useStyles({ dir: dir || direction });

	const option = useMemo(() => _get(form.values, id), [form.values]);

	const onChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
		const newOption = (options ?? groupOptions).find(option => option.id == event.target.value);
		form.setFieldValue(id, newOption);
	}

	return (
		<div ref={setContainerTarget}>
			<div className={clsx(formInputClasses.containLables)}>
				<InputLabel htmlFor={id} className={clsx(formInputClasses.label)}>{description}</InputLabel>
			</div>
			<Select
				id={id}
				disableUnderline
				dir={dir || direction}
				className={classes.inputRoot}
				classes={{
					root: clsx(formInputClasses.input, fieldError && formInputClasses.border, classes.root),
					select: classes.select,
				}}
				inputRef={(el) => { if (innerRef) innerRef.current[id] = el }}
				value={option?.id || -1}
				onChange={onChange}
				IconComponent={() => <ArrowDropDownIcon color="primary" className={classes.icon} />}
				MenuProps={{
					container: containerTarget,
					classes: {
						list: classes.list
					}
				}}
				{...rest}
			>
				<MenuItem disabled value={-1} className={classes.menuItem}>
					{c} {ruleName}
				</MenuItem>
				{options?.map((option, index) => (
					<MenuItem key={index} value={option.id} className={classes.menuItem}>
						{option.value}
					</MenuItem>
				))}
				{groupOptions?.map((option, index) => (
					(option as { type: string }).type === 'header' ? (
						<ListSubheader key={index}>{option.value}</ListSubheader>
					) : (
						<MenuItem key={index} value={(option as Option).id} className={classes.menuItem}>
							{option.value}
						</MenuItem>
					)
				))}
			</Select>
			<FormHelperText text={fieldError} error />
		</div>
	);
}

export default FormSelect;

const useStyles = makeStyles(theme => ({
	inputRoot: {
		width: '100%',
	},

	root: {
		...dirStyleGenerator('padding', '24px !important')
	},

	select: {
		alignItems: 'center',

		[theme.breakpoints.up(500)]: {
			padding: '4px 7px',
			fontSize: 17,
		},
		[theme.breakpoints.down(500)]: {
			padding: '6px 7px',
			fontSize: 22,
		},
		[theme.breakpoints.up(1600)]: {
			padding: '9px 10px',
			fontSize: 23,
		},

		'&:focus': {
			borderRadius: '5px !important',
		}
	},

	icon: {
		position: 'absolute',

		[theme.breakpoints.up(500)]: {
			...dirStyleGenerator('', 4),
			width: 30,
			height: 30
		},
		[theme.breakpoints.down(500)]: {
			...dirStyleGenerator('', 4),
			width: 30,
			height: 30
		},
		[theme.breakpoints.up(1600)]: {
			...dirStyleGenerator('', 10),
			width: 40,
			height: 40
		},
		marginTop: 5,
	},

	list: {
		[theme.breakpoints.up(500)]: {
			padding: '4px 0',
		},
		[theme.breakpoints.down(500)]: {
			padding: '6px 0',
		},
		[theme.breakpoints.up(1600)]: {
			padding: '9px 0',
		}
	},

	menuItem: {
		fontFamily: 'Assistant-Regular',
		color: theme.palette.primary.main,

		[theme.breakpoints.up(500)]: {
			padding: '4px 15px',
			fontSize: 17,
		},
		[theme.breakpoints.down(500)]: {
			padding: '6px 15px',
			fontSize: 22,
		},
		[theme.breakpoints.up(1600)]: {
			padding: '9px 15px',
			fontSize: 23,
		}
	}
}));